import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/odm-cp4d-integration/odm-cp4d-integration/docs/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineNotification = makeShortcode("InlineNotification");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`This chapter documents the environment pre-requisites to deploy the artifacts supporting the integration of WML with ODM.`}</p>
    <h2>{`Environment`}</h2>
    <p>{`In order to run the use case, you will need to have an OpenShift cluster with the following components installed:`}</p>
    <ul>
      <li parentName="ul">{`IBM Cloud Pak for Data 3.0.1`}</li>
      <li parentName="ul">{`IBM Cloud Pak for Automation version 20.0.2`}
        <ul parentName="li">
          <li parentName="ul">{`Operational Decision Manager (ODM)`}</li>
          <li parentName="ul">{`Business Automation Insights (BAI)`}</li>
        </ul>
      </li>
    </ul>
    <h2>{`Configuration`}</h2>
    <InlineNotification kind="warning" mdxType="InlineNotification">
Work in progress
    </InlineNotification>
    <h2>{`Deployment`}</h2>
    <h3>{`WML scoring service deployment`}</h3>
    <p>{`In order to run a scoring service from ODM, you need to create and deploy this scoring service to WML. The easiest way to achieve this is to create a service using the AutoAI capability of CP4D, following these steps:`}</p>
    <ul>
      <li parentName="ul">{`Open a CP4D project and click the `}<inlineCode parentName="li">{`Add to project`}</inlineCode>{` button, then select the `}<inlineCode parentName="li">{`AutoAI experiment`}</inlineCode>{` asset type.`}</li>
      <li parentName="ul">{`Provide a name for you experiment, e.g. `}<inlineCode parentName="li">{`AutoAI GCR`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`On the next screen, when prompted for a data source, drag and drop the training data CSV file `}<inlineCode parentName="li">{`gcr-training-data.csv`}</inlineCode>{` from the `}<a parentName="li" {...{
          "href": "https://github.com/ibm-cloud-architecture/odm-cp4d-integration/tree/master/data/ml-training-data"
        }}><inlineCode parentName="a">{`ml-training-data`}</inlineCode></a>{` folder to the `}<inlineCode parentName="li">{`Add data source`}</inlineCode>{` pane. The data is then loaded and the available data columns are displayed.`}</li>
      <li parentName="ul">{`In `}<inlineCode parentName="li">{`Select prediction column`}</inlineCode>{`, select the `}<inlineCode parentName="li">{`Risk`}</inlineCode>{` column. At this point, you should see the following:
`}<span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "912px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "21.875%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAECAIAAAABPYjBAAAACXBIWXMAAA7DAAAOwwHHb6hkAAAAvElEQVQI1y2Ouw6CQBBF+T8TtbGxsRZFCgsIJtbGV+nPLbvBqODusm8aITEOiyeTybzuzQSMsbIsq6rinDmP9TQeaI21QinIjfujtDaAtQHnnFLKGZNSggZ2g0gIUdfCGgOTN6WiR4JIW6O0Gs6CoihQnmOMEUIYE/ACC/CFCSFEG6eMI/fH81XWtRRKQ5j+iUZbF2S7LEmSNE3jOA7DcP0nilbLzXY/v8rF7Tu7fEaHdnzsfPTF5NRNz+0PISTGZg+Urp0AAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "autoai experiment setting",
            "title": "autoai experiment setting",
            "src": "/odm-cp4d-integration/static/79f154fea835eb76d46f9a982016c91b/94cf0/autoai-experiment-setting.png",
            "srcSet": ["/odm-cp4d-integration/static/79f154fea835eb76d46f9a982016c91b/7fc1e/autoai-experiment-setting.png 288w", "/odm-cp4d-integration/static/79f154fea835eb76d46f9a982016c91b/a5df1/autoai-experiment-setting.png 576w", "/odm-cp4d-integration/static/79f154fea835eb76d46f9a982016c91b/94cf0/autoai-experiment-setting.png 912w"],
            "sizes": "(max-width: 912px) 100vw, 912px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></li>
      <li parentName="ul">{`Click the `}<inlineCode parentName="li">{`Run experiment`}</inlineCode>{` button. When the generation of alternative pipelines complete, select one of them and click `}<inlineCode parentName="li">{`Save as > Model`}</inlineCode>{`, then save the model.
`}<span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1010px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "24.65277777777778%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAFCAIAAADKYVtkAAAACXBIWXMAAA7DAAAOwwHHb6hkAAAA3klEQVQY042Py06EMBSG+9w+gK9hTFy5ce/sZuFao5OZArVlMIVAC/QC0xvUQuLeb/El5z8nJ/mBNUZKpZWSUjrn4r9ZYwSMsbquu65rmia5LEky32nbtqoqSmlNKSFlOmMbnDXXsSNGtwDj7zNEEGYXeMkh+nw/Z3masqIoEEJfpxPBOC8KCCHeID8VfjrQu0d2/8yA1noUmvN+EANvRX3lg+jT/3EYUxHO+DzPYmeaJqWUuU3HD/7w2r28CbDuhOCtd94ts7bee2s3p9wYm+ql7bKEv6ZxDbdg+hjdL7n0FfofKyGKAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "save pipeline",
            "title": "save pipeline",
            "src": "/odm-cp4d-integration/static/8c2c1f660e22f8b7077ff96475d892ff/96101/save-pipeline.png",
            "srcSet": ["/odm-cp4d-integration/static/8c2c1f660e22f8b7077ff96475d892ff/7fc1e/save-pipeline.png 288w", "/odm-cp4d-integration/static/8c2c1f660e22f8b7077ff96475d892ff/a5df1/save-pipeline.png 576w", "/odm-cp4d-integration/static/8c2c1f660e22f8b7077ff96475d892ff/96101/save-pipeline.png 1010w"],
            "sizes": "(max-width: 1010px) 100vw, 1010px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></li>
      <li parentName="ul">{`When prompted by a green message box, click `}<inlineCode parentName="li">{`View in project`}</inlineCode>{` and on the model page, click the `}<inlineCode parentName="li">{`Promote to deployment space`}</inlineCode>{` link.`}</li>
      <li parentName="ul">{`From the main menu (hamburger on the left), you can select `}<inlineCode parentName="li">{`Analytics deployment`}</inlineCode>{` then select the deployment space you used to deploy your newly minted scoring model. You should now see something like this:
`}<span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "854px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "39.93055555555555%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAA7CAAAOwgEVKEqAAAABbElEQVQoz1VRS3KsMAzkNNnmADlVVtnlYO8Ib5flpBJmGP4wDMaDP9iDO5Ip8lFV25bVapWkxHlgVBuEBqQBjPW4SYnhKgA/49+bx+NLwNNrwMNzwP+PFcAdzq9Y1w3LssA5h4QPThSTRH8ZMI4TQURCCCHifg/QS4BxfANrID1ssd12buIocRgu0OqGSVxJbCR/iIJsXJ2TfzJXKnD/Fvv9ZktGaVF0CoJbJihtIaldpRS895jnGcYYKuAipLxBiAla69jiJASs0cR1kZ+MFKzqNkLIGTMJsQAnsJjW5tvfwf72pyBvBt2g0V9V5Cdd2+J0TFEWOdq2JjQoigJ1ze8WZVkgP59xJuR5jqbZ4uwzp6xqVFUT777vkTDheMxQNxeUdUciPbIsiwksmGU5Du8p0s8U2emEruuQpkccDofIqaqShLr4z7NPrKX50PZmQxu0AYvnrfrY2rJY8gHr8GebPDte2j7HfSk8wy+zbWLvmY7rgAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "deployment space",
            "title": "deployment space",
            "src": "/odm-cp4d-integration/static/bc0a957f2d9bba74b1d37ca3d2860fb4/f8087/deployment-space.png",
            "srcSet": ["/odm-cp4d-integration/static/bc0a957f2d9bba74b1d37ca3d2860fb4/7fc1e/deployment-space.png 288w", "/odm-cp4d-integration/static/bc0a957f2d9bba74b1d37ca3d2860fb4/a5df1/deployment-space.png 576w", "/odm-cp4d-integration/static/bc0a957f2d9bba74b1d37ca3d2860fb4/f8087/deployment-space.png 854w"],
            "sizes": "(max-width: 854px) 100vw, 854px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></li>
      <li parentName="ul">{`Click on your model, then on the next page, click the `}<inlineCode parentName="li">{`Create deployment`}</inlineCode>{` button. Select `}<inlineCode parentName="li">{`Online`}</inlineCode>{` for the deployment type, provide a name for the deployment and click `}<inlineCode parentName="li">{`Create`}</inlineCode>{`. After a while, your scoring service is now deployed as a web service. Click on the service to get the endpoint to execute the scoring.
`}<em parentName="li">{`Take note of this endpoint`}</em>{` as it is the one that you will provide to the ODM decision service so it can invoke the scoring service.`}</li>
    </ul>
    <p>{`You can also perform some quick, interactive tests of the scoring service from this page.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "718px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "43.40277777777778%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAIAAAC9o5sfAAAACXBIWXMAAA7DAAAOwwHHb6hkAAAA5UlEQVQoz41Q0W7DIAzk/39xL6uWkKkJJIBtoAVcB6pqT2tPtnWG4zCozfEObDwfcBJK1QaW2DxDrNazCByykOt+ym63sltrjAFEhRTPwCQRIqWUc74DUsr31lqprfYo9cVrKUWq7KpFT7+SPxfJBVYXHCEuiwYM/A5q3ckc2VNxkVtt3Bp/DPU926/LqjdcbDm8R8ScchrTv4OatZ7m+boZ6Aj/AMJLMMRKz5N8niwiwucYFkrsIpEwGZiIzhppOEsP3RK7+ikAjCmVDjWcNmO890Kkis4797cV4px7Xtf/RU7Kmx8Z+gb1Tl9yDAAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "deployed service details",
        "title": "deployed service details",
        "src": "/odm-cp4d-integration/static/643ef6ff317e12ab11def2fe8ce611c1/70c24/deployed-service-details.png",
        "srcSet": ["/odm-cp4d-integration/static/643ef6ff317e12ab11def2fe8ce611c1/7fc1e/deployed-service-details.png 288w", "/odm-cp4d-integration/static/643ef6ff317e12ab11def2fe8ce611c1/a5df1/deployed-service-details.png 576w", "/odm-cp4d-integration/static/643ef6ff317e12ab11def2fe8ce611c1/70c24/deployed-service-details.png 718w"],
        "sizes": "(max-width: 718px) 100vw, 718px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <h3>{`ODM decision services deployment`}</h3>
    <p>{`As mentioned in the use case definition, there are several possible approaches to the design of the object model for the rules. Two decision service archives, a `}<em parentName="p">{`hierarchical`}</em>{` and `}<em parentName="p">{`flat`}</em>{` version, are available under the `}<a parentName="p" {...{
        "href": "https://github.com/ibm-cloud-architecture/odm-cp4d-integration/tree/master/data/exports"
      }}><inlineCode parentName="a">{`exports`}</inlineCode></a>{` folder.
You can load them in ODM Decision Center by following these steps:`}</p>
    <ul>
      <li parentName="ul">{`Open the Decision Center business console and click on the `}<inlineCode parentName="li">{`Library`}</inlineCode>{` tab.`}</li>
      <li parentName="ul">{`Select the `}<inlineCode parentName="li">{`Import Decision Service`}</inlineCode>{` button, navigate to the `}<inlineCode parentName="li">{`dc-export - risk-assessment-main.zip`}</inlineCode>{` archive in the `}<inlineCode parentName="li">{`exports`}</inlineCode>{` folder and click the `}<inlineCode parentName="li">{`Import`}</inlineCode>{` button. Repeat for the same process for the `}<inlineCode parentName="li">{`dc-export - dynamic-risk-assessment-main.zip`}</inlineCode>{` archive.
The decision service projects are now loaded:
`}<span parentName="li" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "930px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "32.29166666666667%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAIAAABM9SnKAAAACXBIWXMAAA7DAAAOwwHHb6hkAAABCklEQVQY012Ky0rDQBiF82C6EW8gLgrtplbFC3iJpG5EvDyWrhR0Y93oO1jTZJK5ms5kbp1GJ5ZK8PBxOP85f9AJL7f6t93opnV0sbYdru6EreiqfX690jtd3z1b7p1s7vc39qKl7vFC+2Cxc9gkSAAYpekwjhEh2lqtjXHOTp3SxkfvxthZUFr/IyiEABADRGjBS22E1Fwq4VF6xljKv+wpGwRsLEQpq6pyDVk7Mb9MnKuq77qa1uZP35n5GmDGHl7ePkYoyVkM6CegcUa/CqFkaY3BlD4O3uOMDFPiJ4AY51xJWaNUgCm7f35NQI4wgRDXjgjnwstonUF89zTIIcohzhFB/puxcq4fuhwwxhFRLZAAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "loaded decision services",
            "title": "loaded decision services",
            "src": "/odm-cp4d-integration/static/7647575ad173f11068aa77b5cbb576d9/9e209/loaded-decision-services.png",
            "srcSet": ["/odm-cp4d-integration/static/7647575ad173f11068aa77b5cbb576d9/7fc1e/loaded-decision-services.png 288w", "/odm-cp4d-integration/static/7647575ad173f11068aa77b5cbb576d9/a5df1/loaded-decision-services.png 576w", "/odm-cp4d-integration/static/7647575ad173f11068aa77b5cbb576d9/9e209/loaded-decision-services.png 930w"],
            "sizes": "(max-width: 930px) 100vw, 930px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      